import { useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';

const ConsentPage = () => {
  const location = useLocation();
  const {
    user,
    role,
    consentLoading,
    requiredTerms,
    missingRequiredTerms,
    requiresConsent,
    recordConsents,
  } = useAuth();

  const termsToRender = useMemo(
    () => (missingRequiredTerms.length ? missingRequiredTerms : requiredTerms),
    [missingRequiredTerms, requiredTerms],
  );

  const [checkedKeys, setCheckedKeys] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!user) return <Navigate to="/login" replace />;
  if (!consentLoading && !requiresConsent) {
    const fallback = role === 'partner' ? '/partner' : role === 'realtor' ? '/realtor' : '/';
    const back = location.state?.from || fallback;
    return <Navigate to={back} replace />;
  }

  const allChecked = termsToRender.length > 0 && checkedKeys.length === termsToRender.length;

  const toggle = (key) => {
    setCheckedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const checkAll = () => {
    if (allChecked) setCheckedKeys([]);
    else setCheckedKeys(termsToRender.map((t) => t.key));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!allChecked) {
      setError('필수 약관에 모두 동의해 주세요.');
      return;
    }
    try {
      setSubmitting(true);
      await recordConsents(termsToRender, 'consent_gate');
      // 동의 후엔 RequireConsent가 자동 통과 → useAuth.requiresConsent=false → 위 Navigate 분기로 원래 경로로 이동
    } catch (err) {
      setError(err.message || '동의 저장 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Wrap>
      <Card as="form" onSubmit={submit}>
        <Header>
          <Eyebrow>이음 필수 약관</Eyebrow>
          <Title>최신 약관에 동의해 주세요</Title>
          <Desc>약관이 변경되었거나 동의 기록이 없는 항목이 있어 다시 확인이 필요합니다.</Desc>
        </Header>

        <CheckAllRow>
          <CheckAllLabel>
            <Checkbox type="checkbox" checked={allChecked} onChange={checkAll} />
            <span>전체 약관에 동의합니다</span>
          </CheckAllLabel>
          <Counter>{checkedKeys.length} / {termsToRender.length}</Counter>
        </CheckAllRow>

        {termsToRender.map((t) => {
          const checked = checkedKeys.includes(t.key);
          return (
            <TermBlock key={t.key}>
              <TermHead>
                <CheckLabel>
                  <Checkbox type="checkbox" checked={checked} onChange={() => toggle(t.key)} />
                  <TermTitle>{t.title}</TermTitle>
                </CheckLabel>
                <Meta>버전 {t.version || '-'} · 시행일 {t.effectiveDate || '-'}</Meta>
              </TermHead>
              {t.content && <Body>{t.content}</Body>}
            </TermBlock>
          );
        })}

        {error && <ErrorText>{error}</ErrorText>}

        <SubmitBtn type="submit" disabled={!allChecked || submitting}>
          {submitting ? '저장 중...' : '동의하고 계속하기'}
        </SubmitBtn>
      </Card>
    </Wrap>
  );
};

export default ConsentPage;

const Wrap = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.borderLight};
  padding: 32px 16px 64px;
  display: flex;
  justify-content: center;
`;

const Card = styled.section`
  width: min(720px, 100%);
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.md};
  padding: 28px 24px 24px;
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Eyebrow = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  letter-spacing: 0.04em;
`;

const Title = styled.h1`
  margin: 8px 0 6px;
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const Desc = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const CheckAllRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  margin-bottom: 14px;
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const CheckAllLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const Counter = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TermBlock = styled.div`
  margin-bottom: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
`;

const TermHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.bgWhite};
`;

const CheckLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

const TermTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const Meta = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textLight};
`;

const Body = styled.div`
  max-height: 200px;
  overflow-y: auto;
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.borderLight};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  white-space: pre-wrap;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const ErrorText = styled.p`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const SubmitBtn = styled.button`
  width: 100%;
  margin-top: 20px;
  padding: 14px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
